/** The signed-out endpoints, as they will exist — standing in for them.
 *
 *  STAGE A. Same contract as the module fixtures: these are the shapes the
 *  Phase 1 backend gets WRITTEN TO RETURN, so swapping to live data is one
 *  line per call and nothing above it changes.
 *
 *  Ported from Fabric `backend/ui/public/routes.py` + the MFA half of
 *  `backend/ui/auth/routes.py`. Behaviours kept deliberately, not copied
 *  carelessly:
 *
 *  - **SEC-01 (forgot-password) is a NON-ANSWER, always.** The response is
 *    identical whether or not the address has an account, because the
 *    difference between "sent" and "no such user" is an account-existence
 *    oracle. Fabric also EQUALISES RESPONSE TIME on this path
 *    (`email_service.py:30`) — a fast "no such user" leaks the same fact
 *    through the clock that the wording no longer leaks. The UI cannot fix
 *    that and must not add a fast path of its own: it always awaits the call.
 *  - **The three token failures are the only three.** `TOKEN_EXPIRED`,
 *    `TOKEN_INVALID`, `PASSWORD_POLICY` — Fabric's `_humanise` map exactly.
 *    An unmapped code is logged and shown as a sentence, never printed raw,
 *    the same rule LoginPage follows.
 *  - **A download token IS the credential.** No session. Eligibility is
 *    re-checked and the build re-resolved AT CLICK TIME, so a link goes stale
 *    the moment access or the release lineup changes. 24 h, capped multi-use.
 *
 *  KNOWN, PORTED AS-IS, FLAGGED FOR STAGE C: these links carry their token in
 *  the URL. A URL reaches browser history, `Referer` on any outbound link, and
 *  every proxy log in between. Fabric does it this way and stage A does not
 *  redesign it — but it is a real exposure, not a style preference, and it is
 *  the kind of thing the per-route gate exists to catch.
 */

/** Fabric's `_humanise` — the three token failures, verbatim in meaning. */
export const TOKEN_ERRORS: Record<string, string> = {
  TOKEN_EXPIRED: "That link has expired. Please request a new one.",
  TOKEN_INVALID: "That link is invalid or has already been used.",
};

/** Fabric's `_humanise_download` — a download link fails differently: it can
 *  also be exhausted, and the person's fix is an administrator, not a retry. */
export const DOWNLOAD_ERRORS: Record<string, string> = {
  TOKEN_EXPIRED:
    "This download link has expired — links are valid for 24 hours. " +
    "Ask your administrator to send a new one.",
  TOKEN_INVALID:
    "This download link is invalid or has reached its download limit. " +
    "Ask your administrator to send a new one.",
};

export function humanise(code: string, message: string | undefined, map: Record<string, string>): string {
  if (code === "PASSWORD_POLICY" && message) return message;   // the server owns this text
  if (!map[code]) console.warn("unmapped public error code:", code);
  return map[code] ?? "That link cannot be used. Ask your administrator for a new one.";
}

/* ------------------------------------------------------------------ *
 * STAGE-A STANDINS. Each becomes one `api.post(...)` in Phase 1.
 * ------------------------------------------------------------------ */

const FAKE_LATENCY = 600;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Always resolves. Never reports whether the address existed — see SEC-01. */
export async function forgotPassword(_loginId: string): Promise<void> {
  await sleep(FAKE_LATENCY);
}

/** Token + new password. Throws `{code, message}` on the three failures. */
export async function resetPassword(token: string, _password: string): Promise<{ is_admin: boolean }> {
  await sleep(FAKE_LATENCY);
  if (!token) throw { code: "TOKEN_INVALID" };
  if (token === "expired") throw { code: "TOKEN_EXPIRED" };
  return { is_admin: true };
}

export async function verifyEmail(token: string): Promise<{ is_admin: boolean }> {
  await sleep(FAKE_LATENCY);
  if (!token) throw { code: "TOKEN_INVALID" };
  if (token === "expired") throw { code: "TOKEN_EXPIRED" };
  return { is_admin: true };
}

export type DownloadTarget = { filename: string; product: string; version: string; size_bytes: number };

export async function redeemDownload(token: string): Promise<DownloadTarget> {
  await sleep(FAKE_LATENCY);
  if (!token || token === "invalid") throw { code: "TOKEN_INVALID" };
  if (token === "expired") throw { code: "TOKEN_EXPIRED" };
  return { filename: "uds-tool-setup-2.4.1.exe", product: "UDS Tool", version: "2.4.1", size_bytes: 184_549_376 };
}

/* ---------------------------- MFA enrolment ---------------------------- */

export type EnrolStart = { secret: string; otpauth_uri: string; qr_png_path: string };

/** Fabric holds the pending secret in the SESSION until a code verifies against
 *  it (`session["mfa_enrol_secret"]`), so a reload re-uses the same secret
 *  instead of silently re-keying the authenticator the person just scanned. */
export async function startEnrol(loginId: string): Promise<EnrolStart> {
  await sleep(400);
  const secret = "JBSWY3DPEHPK3PXP";
  return {
    secret,
    otpauth_uri: `otpauth://totp/${encodeURIComponent(loginId)}?secret=${secret}&issuer=Aphelion`,
    qr_png_path: "/auth/mfa/qr.png",
  };
}

/** Verify the first code. On success Fabric stores the secret, issues backup
 *  codes, EMAILS A SECURITY ALERT, and sets a single-use
 *  `mfa_enrol_just_completed` flag.
 *
 *  That flag is not bookkeeping — it is a patched MFA BYPASS. `finish-enrol`
 *  was reachable straight from a password-only stage-1 session and granted a
 *  full admin session with no code ever verified, for every already-enrolled
 *  admin (`tests/security/test_mfa_finish_enrol_bypass.py`). So the UI must
 *  never treat "I showed the backup codes screen" as proof of enrolment: the
 *  proof is server-side and single-use, and `finishEnrol` can legitimately
 *  refuse.
 */
export async function verifyEnrol(_code: string): Promise<{ backup_codes: string[] }> {
  await sleep(FAKE_LATENCY);
  return {
    backup_codes: [
      "4f9c-2a11", "8d3e-7b60", "1c74-9fa2", "6e02-3d85",
      "b917-5c40", "2a68-eb13", "7d51-0f9c", "e340-86b7",
    ],
  };
}
